import { useEffect, useState } from 'react'
import { api, type AuditorResumo } from '../api/cliente'
import { Cabecalho, Carregando, Erro } from '../componentes/Basicos'
import { Confirmar } from '../componentes/Confirmar'

/**
 * As contas dos auditores.
 *
 * **Não há papéis**, e é a decisão que já estava escrita: poucos auditores, uma empresa só. Quem
 * está autenticado administra contas. O que esta tela fecha é a porta de fora — o cadastro anônimo
 * do Identity, que agora vale para a primeira conta da instalação e para mais nenhuma.
 *
 * **Desativar não é apagar.** O laudo diz quem fechou o quadro, e esse id não é chave estrangeira:
 * apagar a conta que assinou deixaria a assinatura sem dono. Por isso a conta que já trabalhou só
 * desativa — a lista mostra o que cada uma assinou, e é o que explica o botão que falta.
 */
export function Auditores({ aoVoltar }: { aoVoltar: () => void }) {
  const [auditores, setAuditores] = useState<AuditorResumo[] | null>(null)
  const [erro, setErro] = useState<unknown>(null)
  const [ocupado, setOcupado] = useState(false)
  const [recarga, setRecarga] = useState(0)

  const [criando, setCriando] = useState(false)
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')

  const [aApagar, setAApagar] = useState<AuditorResumo | null>(null)
  const [aTrocarSenha, setATrocarSenha] = useState<AuditorResumo | null>(null)
  const [senhaNova, setSenhaNova] = useState('')

  useEffect(() => {
    let vivo = true
    api
      .auditores()
      .then((lista) => vivo && setAuditores(lista))
      .catch((falha: unknown) => vivo && setErro(falha))
    return () => {
      vivo = false
    }
  }, [recarga])

  /** Toda ação recarrega: a lista traz o que cada conta assinou, e isso muda debaixo dela. */
  async function faz(acao: () => Promise<unknown>) {
    setOcupado(true)
    setErro(null)
    try {
      await acao()
      setRecarga((n) => n + 1)
      return true
    } catch (falha) {
      setErro(falha)
      return false
    } finally {
      setOcupado(false)
    }
  }

  if (!auditores) return <Carregando o="os auditores" />

  return (
    <>
      <Cabecalho
        titulo="Auditores"
        sub={auditores.length === 1 ? '1 conta' : `${auditores.length} contas`}
        acoes={
          <>
            <button className="btn g sm" onClick={aoVoltar}>
              Visitas
            </button>
            <button className="btn g sm" onClick={() => api.sair()}>
              Sair
            </button>
          </>
        }
      />
      <div className="corpo">
        <div className="limite">
          <Erro erro={erro} aoFechar={() => setErro(null)} />

          <div className="barra">
            <button className="btn p" disabled={ocupado} onClick={() => setCriando(!criando)}>
              {criando ? 'Cancelar' : 'Criar conta'}
            </button>
          </div>

          {criando && (
            <form
              className="card"
              onSubmit={async (evento) => {
                evento.preventDefault()
                if (!email.trim() || !senha) return
                if (await faz(() => api.criarAuditor(email.trim(), senha))) {
                  setEmail('')
                  setSenha('')
                  setCriando(false)
                }
              }}
            >
              <div className="campo">
                <label htmlFor="novoEmail">E-mail</label>
                <input
                  id="novoEmail"
                  type="email"
                  autoComplete="off"
                  value={email}
                  onChange={(evento) => setEmail(evento.target.value)}
                />
              </div>
              <div className="campo">
                <label htmlFor="novaSenha">Senha</label>
                <input
                  id="novaSenha"
                  type="password"
                  autoComplete="new-password"
                  value={senha}
                  onChange={(evento) => setSenha(evento.target.value)}
                />
                <small>
                  Pelo menos 6 caracteres, com maiúscula, minúscula, número e símbolo. É a regra do
                  Identity, não nossa.
                </small>
              </div>
              <button className="btn p largo" type="submit" disabled={ocupado}>
                {ocupado ? 'criando…' : 'Criar'}
              </button>
            </form>
          )}

          <ul className="lista">
            {auditores.map((auditor) => (
              <li key={auditor.id} className="item">
                <div className="dados">
                  <div className="linha1">
                    <b>{auditor.email}</b>
                    {!auditor.ativo && <span className="selo">desativada</span>}
                  </div>
                  <div className="linha2">{assinou(auditor)}</div>
                </div>

                <div className="acoesDoItem">
                  <button
                    className="btn g sm"
                    disabled={ocupado}
                    onClick={() => setATrocarSenha(auditor)}
                  >
                    Senha
                  </button>
                  <button
                    className="btn sm"
                    disabled={ocupado}
                    onClick={() =>
                      faz(() => api.alterarAuditor(auditor.id, auditor.email, !auditor.ativo))
                    }
                  >
                    {auditor.ativo ? 'Desativar' : 'Reativar'}
                  </button>
                  {auditor.visitas === 0 && auditor.quadros === 0 && (
                    <button className="btn g sm" disabled={ocupado} onClick={() => setAApagar(auditor)}>
                      Apagar
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {aApagar && (
        <Confirmar
          titulo="Apagar esta conta?"
          rotulo="Apagar a conta"
          ocupado={ocupado}
          aoConfirmar={async () => {
            if (await faz(() => api.apagarAuditor(aApagar.id))) setAApagar(null)
          }}
          aoCancelar={() => setAApagar(null)}
        >
          <p>
            <b>{aApagar.email}</b>
          </p>
          <p>
            Esta conta nunca criou visita nem fechou quadro, então some sem deixar assinatura órfã.
            Quem já assinou alguma coisa só pode ser desativado.
          </p>
        </Confirmar>
      )}

      {aTrocarSenha && (
        <Confirmar
          titulo="Definir uma senha nova"
          rotulo="Trocar a senha"
          ocupado={ocupado}
          aoConfirmar={async () => {
            if (!senhaNova) return
            if (await faz(() => api.trocarSenha(aTrocarSenha.id, senhaNova))) {
              setATrocarSenha(null)
              setSenhaNova('')
            }
          }}
          aoCancelar={() => {
            setATrocarSenha(null)
            setSenhaNova('')
          }}
        >
          <p>
            <b>{aTrocarSenha.email}</b>
          </p>
          <p>
            A senha antiga não é pedida: quem troca aqui é quem administra, e o caso é o auditor que
            esqueceu a dele. A antiga para de valer na hora.
          </p>
          <div className="campo">
            <label htmlFor="senhaTrocada">Senha nova</label>
            <input
              id="senhaTrocada"
              type="password"
              autoComplete="new-password"
              value={senhaNova}
              onChange={(evento) => setSenhaNova(evento.target.value)}
            />
          </div>
        </Confirmar>
      )}
    </>
  )
}

/**
 * O que a conta assinou. **É isto que explica o botão de apagar existir ou não** — quem tem laudo
 * atrás do nome não some, porque o laudo diz quem o fechou.
 */
function assinou(auditor: AuditorResumo) {
  if (auditor.visitas === 0 && auditor.quadros === 0) return 'nunca assinou nada'

  const visitas = auditor.visitas === 1 ? '1 visita' : `${auditor.visitas} visitas`
  const quadros = auditor.quadros === 1 ? '1 quadro' : `${auditor.quadros} quadros`
  return `${visitas} · ${quadros} — só pode ser desativada`
}
